const RAPID_TAP_WINDOW_MS = 800;
const lastTapByKey = new Map();

const TRACKING_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
];

const normalizeApiBase = (apiBase = process.env.REACT_APP_LQR_AI_ORIGIN) => {
    const base = String(apiBase || "").trim();
    return base ? base.replace(/\/+$/, "") : "";
};

const normalizeOriginUrl = (originUrl = "") => {
    const value = String(originUrl || "").trim();
    return /^https?:\/\//i.test(value) ? value : "";
};

const normalizeAuthToken = (authToken = "") => String(authToken || "").trim();
const normalizeUrl = (url = "") => String(url || "").trim();
const isHttpUrl = (url = "") => /^https?:\/\//i.test(normalizeUrl(url));

export const shouldIgnoreRapidTap = (key, windowMs = RAPID_TAP_WINDOW_MS) => {
    if (!key) return false;

    const now = Date.now();
    const lastTapAt = lastTapByKey.get(key) || 0;
    const tapWindow = typeof windowMs === "number" ? windowMs : RAPID_TAP_WINDOW_MS;

    if (now - lastTapAt < tapWindow) {
        return true;
    }

    lastTapByKey.set(key, now);
    return false;
};

export const buildConnectTrackingPayload = ({
    originUrl = "",
    utm_source = "",
    utm_medium = "",
    utm_campaign = "",
    utm_content = "",
    utm_term = "",
} = {}) => {
    const tracking = {};
    const safeOriginUrl = normalizeOriginUrl(originUrl);

    if (safeOriginUrl) {
        tracking.originUrl = safeOriginUrl;
    }

    const values = {
        utm_source,
        utm_medium,
        utm_campaign,
        utm_content,
        utm_term,
    };

    TRACKING_KEYS.forEach((key) => {
        const value = String(values[key] || "").trim();
        if (value) {
            tracking[key] = value;
        }
    });

    return tracking;
};

export const sendConnectButtonEvent = (apiBase, payload = {}) => {
    const base = normalizeApiBase(apiBase);
    const qrId = String(payload?.qrId || "").trim();
    const buttonId = String(payload?.buttonId || "").trim();
    const event = String(payload?.event || "").trim();
    const authToken = normalizeAuthToken(payload?.authToken);

    if (!base || !qrId || !buttonId || !event) {
        return false;
    }

    const bodyPayload = {
        qrId,
        buttonId,
        event,
        ...buildConnectTrackingPayload(payload),
    };
    const url = `${base}/connect-button-event`;
    const body = JSON.stringify(bodyPayload);

    if (!authToken && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        try {
            const blob = new Blob([body], { type: "application/json" });
            const queued = navigator.sendBeacon(url, blob);
            if (queued) {
                return true;
            }
        } catch {
            // Fall through to fetch below.
        }
    }

    if (typeof fetch === "function") {
        fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...(authToken ? { Authorization: `Bearer ${authToken}` } : ''),
            },
            body,
            keepalive: true,
        }).catch(() => {
            // Best-effort only.
        });

        return true;
    }

    return false;
};

export const fetchConnectClickDestination = async (clickUrl = "", authToken = "") => {
    const normalizedClickUrl = normalizeUrl(clickUrl);
    const normalizedToken = normalizeAuthToken(authToken);

    if (!normalizedClickUrl || typeof fetch !== "function") {
        return "";
    }

    try {
        const response = await fetch(normalizedClickUrl, {
            method: "GET",
            headers: normalizedToken ? { Authorization: `Bearer ${normalizedToken}` } : {},
            redirect: "follow",
            keepalive: true,
        });

        if (response.redirected && isHttpUrl(response.url)) {
            return response.url;
        }

        const contentType = response.headers.get("content-type") || "";

        if (contentType.includes("application/json")) {
            const data = await response.json();
            const candidate =
                data?.url
                || data?.redirectUrl
                || data?.location
                || data?.targetUrl
                || "";

            return isHttpUrl(candidate) ? candidate : "";
        }

        const text = (await response.text()).trim();
        return isHttpUrl(text) ? text : "";
    } catch {
        return "";
    }
};
